import { useEffect, useMemo, useState } from "react";
import {
    FaCheckCircle,
    FaChevronRight,
    FaClock,
    FaPlus,
    FaReceipt,
    FaTimesCircle,
    FaWallet,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Pagination from "../../components/Pagination";
import { ExpenseMobileCard, ExpenseTableRow, SummaryCard } from "../../components/ExpenseComponent";
import { formatNaira } from "../../utils/bill";
import { EXPENSE_CATEGORIES, STATUS } from "../../constants";
import { sendRequest } from "../../utils/api";
import { ExpenseDetails } from "../../models/expense/ExpenseDetail";
import FullPageSpinner from "../../components/FullPageSpinner";
import { useModal } from "../../context/ConfirmModalContext";
import FilterModal from "../../Modal/FilterModal";
import SearchFilterBar from "../../Modal/SearchFilterBar";
import Site from "../../models/Site";
import { capitalizeWords } from "../../utils/string";


const ExpensesPage = () => {
    const navigate = useNavigate();
    const { openModal, closeModal } = useModal();
    // const { showToast } = useToast();

    const [expenses, setExpenses] = useState<ExpenseDetails[]>([]);
    const [sites, setSites] = useState<Site[]>([]);

    // FILTER STATE
    const [filterInit] = useState({
        search: "",
        status: "All Statuses",
        category: "All Categories",
        site: "All Sites"
    });
    const [search, setSearch] = useState(filterInit.search);
    const [category, setCategory] = useState(filterInit.category);
    const [status, setStatus] = useState(filterInit.status);
    const [site, setSite] = useState(filterInit.site);

    const [draftSite, setDraftSite] = useState(site);
    const [draftStatus, setDraftStatus] = useState(status);
    const [draftCategory, setDraftCategory] = useState(category);

    const [showFilters, setShowFilters] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);

    const [page, setPage] = useState(1);
    const [itemsPerPage] = useState(5);

    useEffect(() => {
        const getExpense = async () => {
            try {
                setLoading(true);
                const expenses = await sendRequest("GET", "/expenses");
                setExpenses(expenses);

                // Load sites in the background without blocking loading completion
                void sendRequest("GET", "/sites")
                    .then((sites) => {
                        setSites(sites);
                    })
                    .catch((err) => {
                        console.error("Failed to load sites:", err);
                    });
            } catch (err: any) {

            } finally {
                setLoading(false);
            }
        }
        getExpense();
    }, []);



    const openFilters = () => {
        // Load currently applied filters into the draft
        setDraftSite(site);
        setDraftStatus(status);
        setDraftCategory(category);

        setShowFilters(true);
    };

    const applyFilters = () => {
        setSite(draftSite);
        setStatus(draftStatus);
        setCategory(draftCategory);

        setShowFilters(false);
        // setSelectedStudents([]);
    };

    const cancelFilters = () => {
        // Discard draft changes
        setSearch("");
        setDraftSite(site);
        setDraftCategory(category);
        setDraftStatus(status);

        setShowFilters(false);
    };

    const clearFilters = () => {
        setDraftSite(filterInit.site);
        setDraftCategory(filterInit.category);
        setDraftStatus(filterInit.status);
    };


    const expenseFilterFields = [
        {
            key: "category",
            label: "Category",
            value: draftCategory,
            onChange: setDraftCategory,
            options: [filterInit.category, ...EXPENSE_CATEGORIES],
        },

        {
            key: "status",
            label: "Status",
            value: draftStatus,
            onChange: setDraftStatus,
            options: [filterInit.status, ...STATUS],
        },

        {
            key: "site",
            label: "Site / Location",
            value: draftSite,
            onChange: setDraftSite,
            options: [
                filterInit.site, 
                ...sites.map(site => capitalizeWords(site.name))
            ],
        },
    ];

    const filteredExpenses = useMemo(() => {
        const query = search.trim().toLowerCase();

        return expenses.filter((expense) => {
            const exp = expense.expense;
            const requester = expense?.requestedBy;

            const requesterName = (
                `${requester.firstName} ${requester.lastName}`
                    .toLocaleLowerCase()
            );

            const expenseCategory = exp?.category?.toLocaleLowerCase();
            const expenseStatus = exp?.status?.toLocaleLowerCase();
            const siteName = expense?.site?.name?.toLocaleLowerCase();

            const matchesSearch =
                !query ||
                exp?.title?.toLowerCase().includes(query) ||
                requesterName.includes(query);

            const matchesCategory =
                category === "All Categories" ||
                expenseCategory === category.toLocaleLowerCase();

            const matchesStaus =
                status === "All Statuses" ||
                expenseStatus === status.toLocaleLowerCase();

            const matchesSite =
                site === "All Sites" ||
                siteName === site.toLocaleLowerCase();

            return (
                matchesSearch &&
                matchesCategory &&
                matchesStaus &&
                matchesSite
            );
        });
    }, [expenses, search, category, status]);


    const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage);


    const paginatedExpenses = useMemo(() => {
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;

        return filteredExpenses.slice(startIndex, endIndex);
    }, [filteredExpenses, page, itemsPerPage]);

    const statistics = useMemo(() => {
        const total = expenses.reduce(
            (sum, expense) => sum + (
                expense.expense.approvedAmount ??
                expense.expense.requestedAmount
            ), 0
        );

        const pending = expenses
            .filter((expense) => expense?.expense?.status === "pending")
            .reduce((sum, expense) => sum + (
                expense.expense.approvedAmount ??
                expense.expense.requestedAmount
            ), 0);

        const approved = expenses
            .filter(
                (expense) =>
                    expense?.expense?.status === "approved" ||
                    expense?.expense.status === "paid"
            )
            .reduce((sum, expense) => sum + (
                expense.expense.approvedAmount ??
                expense.expense.requestedAmount
            ), 0);

        const rejected = expenses
            .filter((expense) => expense?.expense?.status === "rejected")
            .reduce((sum, expense) => sum + (
                expense.expense.approvedAmount ??
                expense.expense.requestedAmount
            ), 0);

        return {
            total,
            pending,
            approved,
            rejected,
        };
    }, [expenses]);


    const expenseSummary = () => navigate("/expenses/summary");


    return (
        <div className="min-h-screen min-w-0 bg-slate-50 text-slate-900">
            <main className="w-full min-w-0 px-4 py-4 sm:px-8 sm:py-6 lg:px-6">
                <FullPageSpinner show={loading} />
                {/* Header */}
                <div className="mb-7 flex flex-row items-center justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800">
                            Expenses
                        </h1>
                    </div>

                    <button
                        type="button"
                        onClick={() => navigate("/expenses/create")}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
                    >
                        {FaPlus({
                            size: 11,
                            className: "",
                        })}

                        <span className="hidden sm:inline">
                            New Expense
                        </span>

                        <span className="sm:hidden">
                            New Expense
                        </span>
                    </button>
                </div>

                <div className="mb-3 hidden items-center justify-between lg:flex">
                    <p className="text-xs font-semibold text-slate-600">
                        Expense Overview
                    </p>

                    <button
                        type="button"
                        onClick={expenseSummary}
                        className="flex items-center gap-1 text-xs font-medium text-blue-600 transition hover:text-blue-700"
                    >
                        View full summary

                        {FaChevronRight({
                            size: 10,
                            className: "",
                        })}
                    </button>
                </div>
                <div className="hidden lg:grid lg:grid-cols-4 lg:gap-4">

                    <SummaryCard
                        title="Total Expenses"
                        value={formatNaira(statistics.total)}
                        subtitle={`${expenses.length} expense claims`}
                        icon={FaWallet({
                            size: 17,
                            className: "text-blue-600",
                        })}
                        iconClass="bg-blue-50"
                    />

                    <SummaryCard
                        title="Pending"
                        value={formatNaira(statistics.pending)}
                        subtitle="Awaiting approval"
                        icon={FaClock({
                            size: 17,
                            className: "text-amber-600",
                        })}
                        iconClass="bg-amber-50"
                    />

                    <SummaryCard
                        title="Approved / Paid"
                        value={formatNaira(statistics.approved)}
                        subtitle="Approved expenses"
                        icon={FaCheckCircle({
                            size: 17,
                            className: "text-emerald-600",
                        })}
                        iconClass="bg-emerald-50"
                    />

                    <SummaryCard
                        title="Rejected"
                        value={formatNaira(statistics.rejected)}
                        subtitle="Rejected claims"
                        icon={FaTimesCircle({
                            size: 17,
                            className: "text-red-500",
                        })}
                        iconClass="bg-red-50"
                    />
                </div>

                <div className="lg:hidden">
                    <button
                        type="button"
                        onClick={expenseSummary}
                        className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-blue-200 hover:bg-blue-50/30"
                    >
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50">
                                {FaWallet({
                                    size: 17,
                                    className: "text-blue-600",
                                })}
                            </div>

                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-slate-800">
                                    Expense Summary
                                </p>

                                <p className="mt-0.5 text-xs text-slate-400">
                                    View expense overview and reports
                                </p>
                            </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-1 text-xs font-medium text-blue-600">
                            <span>View</span>

                            {FaChevronRight({
                                size: 11,
                                className: "",
                            })}
                        </div>
                    </button>
                </div>

                {/* Expense List */}
                <section className="mt-6 w-full min-w-0 max-w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    {/* Toolbar */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">

                        {expenses.length > 0 && <div className="border-b border-slate-200 p-4 sm:p-5">
                            <div className="relative">

                                {/* SEARCH BAR */}
                                <SearchFilterBar
                                    search={search}
                                    onSearchChange={setSearch}
                                    placeholder="Search by expense title or requester..."
                                    onFilterClick={openFilters}
                                    filterActive={
                                        !status.toLocaleLowerCase().startsWith("all") ||
                                        !site.toLocaleLowerCase().startsWith("all") ||
                                        !category.toLocaleLowerCase().startsWith("all")
                                    }
                                />

                                {/* FILTER POPOVER */}
                                <FilterModal
                                    isOpen={showFilters}
                                    title="Filter Expenses"
                                    description="Refine the expenses displayed below"
                                    fields={expenseFilterFields}
                                    onApply={applyFilters}
                                    onCancel={cancelFilters}
                                    onClear={clearFilters}
                                />
                            </div>
                        </div>}

                        {/* Result Header */}
                        <div className="flex items-center justify-between px-4 py-3 sm:px-5">
                            <div>
                                <p className="text-xs font-semibold text-slate-700">
                                    Expense Claims
                                </p>

                                <p className="mt-0.5 text-[11px] text-slate-400">
                                    {filteredExpenses.length} result
                                    {filteredExpenses.length !== 1 ? "s" : ""}
                                </p>
                            </div>
                        </div>
                        {/* Desktop Table */}
                        <div className="hidden overflow-x-auto md:block">
                            {paginatedExpenses.length > 0 ? (
                                <div className="w-full min-w-0 max-w-full overflow-hidden rounded-xl border border-slate-200 bg-white">
                                    <div className="w-full max-w-full overflow-x-auto">
                                        <table className="w-full min-w-[900px] table-auto">
                                            <thead>
                                                <tr className="bg-slate-50 text-left">
                                                    <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                                        Expense
                                                    </th>

                                                    <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                                        Category
                                                    </th>

                                                    <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                                        Amount
                                                    </th>

                                                    <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                                        Date
                                                    </th>

                                                    <th className="px-5 py-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                                        Approval
                                                    </th>

                                                    <th className="w-12 px-5 py-3"></th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {paginatedExpenses.map((expense) => (
                                                    <ExpenseTableRow
                                                        key={expense?.expense?.id}
                                                        requestedBy={expense?.requestedBy}
                                                        approvers={expense.approvers}
                                                        expense={expense?.expense}
                                                        onClick={() =>
                                                            navigate(`/expenses/${expense?.expense?.id}`)
                                                        }
                                                    />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                <EmptyState />
                            )}
                        </div>

                        {/* Mobile */}
                        <div className="md:hidden">
                            {paginatedExpenses.length > 0 ? (
                                paginatedExpenses.map((expense) => (
                                    <ExpenseMobileCard
                                        key={expense?.expense?.id}
                                        requestedBy={expense?.requestedBy}
                                        approvers={expense.approvers}
                                        expense={expense.expense}
                                        onClick={() =>
                                            navigate(`/expenses/${expense.expense?.id}`)
                                        }
                                    />
                                ))
                            ) : (
                                <EmptyState />
                            )}
                        </div>

                        {/* PAGINATION BUTTON */}
                        {filteredExpenses.length > itemsPerPage && (
                            <Pagination
                                page={page}
                                totalPages={totalPages}
                                totalItems={filteredExpenses.length}
                                itemsPerPage={itemsPerPage}
                                itemLabel="students"
                                visiblePages={3}
                                hasPreviousPage={page > 1}
                                hasNextPage={page < totalPages}
                                onPrevious={() => setPage((prev) => prev - 1)}
                                onNext={() => setPage((prev) => prev + 1)}
                                onPageChange={setPage}
                            />
                        )}
                    </div>
                </section>
            </main>
        </div>
    );
};

const EmptyState = () => {
    return (
        <div className="flex min-h-[280px] flex-col items-center justify-center px-5 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50">
                {FaReceipt({
                    size: 19,
                    className: "text-blue-500",
                })}
            </div>

            <h3 className="mt-4 text-sm font-semibold text-slate-700">
                No expenses found
            </h3>

            <p className="mt-1 max-w-sm text-xs leading-5 text-slate-400">
                No expense claims match your current search or
                filters.
            </p>
        </div>
    );
};

export default ExpensesPage;